import { computed, onBeforeUnmount, onMounted } from 'vue';
import { throttle } from 'lodash-es';
import { storeToRefs } from 'pinia';

import { useInternational } from '@/common/locale';
import router from '@/common/router';
import { ONE_MINUTE, ONE_SECOND } from '@/common/utils/define';
import { useMessageBox } from '@/common/inject/useMessageBox';

import { useAuthStore } from '@/common/stores/auth';
import { usePropertyStore } from '@/common/stores/property';
import { useSessionTimerStore } from '@/common/stores/session-timer';

import { useRepeat } from '@/worker/composables/useRepeat';
import { useMyInfoStore } from '@/config/stores/my-info';

const FETCH_CYCLE = ONE_SECOND;
const FIVE_MINUTES = 5 * (ONE_MINUTE / ONE_SECOND);

const setup = () => {
  const { myInfo } = storeToRefs(useMyInfoStore());
  const { getToken } = useAuthStore();

  const { t } = useInternational();
  const { confirmMsg } = useMessageBox();

  const rootElement = document.querySelector('body');
  const tokenInfo = computed(() => ({
    sessionToken: getToken('sessionToken') ?? '',
    accessToken: getToken('accessToken'),
    refreshToken: getToken('refreshToken'),
  }));

  const sessionTimerStore = useSessionTimerStore();
  const { fetchSessionTime, updateSessionTime } = sessionTimerStore;
  const {
    isOpenModal,
    isTimerRunning,
    isExpiredSessionTime,
    displaySessionTime,
    remainingSessionTime,
  } = storeToRefs(sessionTimerStore);
  const isVisibleTimer = computed<boolean>(
    () => usePropertyStore().isSessionTimerActive() && isTimerRunning.value,
  );

  const isSetSessionTimeout = computed(() => myInfo.value.sessionTimeout !== -1);

  const isLessThanFiveMinutes = computed<boolean>(() => remainingSessionTime.value < FIVE_MINUTES);
  useRepeat(() => fetchSessionTime(tokenInfo.value.sessionToken), FETCH_CYCLE, {
    isSetup: true,
    isImmediate: true,
  });

  const logout = async () => {
    await router.push('/logout');
    isOpenModal.value = false;
  };

  const invalidSessionTimeHandler = (event) => {
    if (!event.target.closest('.ev-message-box')) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!isOpenModal.value) {
      confirmMsg({
        msgStr: t('MESSAGE.SESSION_TIMEOUT'),
        showCancelBtn: false,
        useHTML: true,
        okCallback: logout,
        cancelCallback: logout, // esc 키 눌렀을 때 로그아웃 처리
      });

      isOpenModal.value = true;
    }
  };

  const loginSessionTimeCheck = throttle(async (event): Promise<void> => {
    if (!isVisibleTimer.value) {
      return;
    }

    if (isExpiredSessionTime.value) {
      invalidSessionTimeHandler(event);
      return;
    }

    if (!isExpiredSessionTime.value && tokenInfo.value.sessionToken) {
      await updateSessionTime({
        refreshToken: tokenInfo.value.refreshToken,
        sessionToken: tokenInfo.value.sessionToken,
        accessToken: tokenInfo.value.accessToken,
      });
      await fetchSessionTime(tokenInfo.value.sessionToken);
    }
  }, ONE_SECOND);

  onMounted(() => {
    if (usePropertyStore().isSessionTimerActive()) {
      isTimerRunning.value = true;
      rootElement?.addEventListener('click', loginSessionTimeCheck, true);
    }
  });

  onBeforeUnmount(() => {
    if (usePropertyStore().isSessionTimerActive()) {
      isTimerRunning.value = false;
      rootElement?.removeEventListener('click', loginSessionTimeCheck, true);
    }
  });

  return {
    t,
    isVisibleTimer,
    isSetSessionTimeout,
    isExpiredSessionTime,
    isLessThanFiveMinutes,
    displaySessionTime,
  };
};

export { setup };
